import { useState } from 'react';
import { createService, listServices, updateService } from '../lifeos/api.js';
import { dueChip, humanize, money, monthlyCents, ymdInHomeZone } from '../lifeos/format.js';
import FormSheet from '../lifeos/FormSheet.js';
import { cents, options, text, toValues } from '../lifeos/forms.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Button, EmptyState, Group, IconButton, Loader, Page, Row, Fold, Meta, useParamSetter, useSphereLens, When } from '../ui/index.js';
import type { Service, ServiceCadence, ServiceStatus } from '../lifeos/types.js';

const CADENCES: ServiceCadence[] = ['monthly', 'yearly', 'usage', 'one_time', 'free'];
const STATUSES: ServiceStatus[] = ['active', 'trial', 'cancelled'];

/**
 * Services (`/services`): subscriptions and vendors with what they cost per
 * month. Renewal reminders come from the derived obligation (spec §3.3), so a
 * renewal date set here shows up in Upcoming and the briefs on its own.
 */
export default function Services(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const sphere = params.get('sphere');
  const spheres = useSpheres();
  const lens = useSphereLens(spheres, sphere, (k) => setParam('sphere', k));
  const list = useLoad(() => listServices({ sphere: sphere ?? undefined }), [sphere]);
  const [editing, setEditing] = useState<Service | 'new' | null>(null);
  const today = ymdInHomeZone(new Date());
  const live = list.data?.filter((s) => s.status !== 'cancelled') ?? [];
  const perMonth = live.reduce((sum, s) => sum + monthlyCents(s), 0);

  return (
    <Page
      title="Services"
      sub={
        list.data && live.length > 0 ? (
          <>
            <span className="la-total__n num">{money(perMonth)}</span> a month · {live.length} active
          </>
        ) : undefined
      }
      actions={
        <>
          {lens.button}
          <IconButton icon="plus" label="Add" onClick={() => setEditing('new')} />
        </>
      }
    >
      {lens.chip && <div className="ui-chips">{lens.chip}</div>}
      <Loader state={list}>
        {(items) => {
          if (items.length === 0)
            return (
              <EmptyState
                icon="repeat"
                title="No services yet."
                hint="Add subscriptions to see what renews when."
                action={
                  <Button variant="secondary" icon="plus" onClick={() => setEditing('new')}>
                    Add one
                  </Button>
                }
              />
            );
          const cancelled = items.filter((s) => s.status === 'cancelled');
          const row = (s: Service): JSX.Element => (
            <ServiceRow key={s.id} s={s} today={today} color={sphereColor(spheres, s.sphereKey)} onEdit={() => setEditing(s)} />
          );
          return (
            <>
              <Group label="Active services">{live.map(row)}</Group>
              <Group header="Cancelled" label="Cancelled">
                {cancelled.length > 0 && <Fold items={cancelled} n={0} noun="cancelled" render={row} />}
              </Group>
            </>
          );
        }}
      </Loader>
      {lens.sheet}
      {editing && (
        <FormSheet
          title={editing === 'new' ? 'Add a service' : `Edit ${editing.name}`}
          submitLabel={editing === 'new' ? 'Add' : 'Save'}
          fields={[
            { key: 'name', label: 'Name', required: true, wide: true },
            { key: 'costCents', label: 'Cost ($)', kind: 'money' },
            { key: 'cadence', label: 'Billed', kind: 'select', options: options(CADENCES, humanize) },
            { key: 'renewsOn', label: 'Renews on', kind: 'date' },
            { key: 'status', label: 'Status', kind: 'select', options: options(STATUSES, humanize) },
            { key: 'url', label: 'Website', placeholder: 'https://…', wide: true },
            {
              key: 'credentialRef',
              label: 'Where the login lives',
              placeholder: 'e.g. vaultwarden:stripe',
              hint: 'A label only, never a password or key.',
              wide: true,
            },
            { key: 'sphereKey', label: 'Sphere', kind: 'select', options: spheres.map((s) => ({ value: s.key, label: s.name })) },
            { key: 'notes', label: 'Notes', kind: 'textarea' },
          ]}
          initial={
            editing === 'new'
              ? { cadence: 'monthly', status: 'active', sphereKey: 'rova' }
              : toValues({ ...editing, credentialRef: editing.credentialRef === '[redacted]' ? '' : editing.credentialRef }, ['costCents'])
          }
          onClose={() => setEditing(null)}
          onSubmit={async (v) => {
            const body = {
              name: text(v.name)!,
              costCents: cents(v.costCents, 'Cost'),
              cadence: v.cadence as ServiceCadence,
              renewsOn: text(v.renewsOn),
              status: v.status as ServiceStatus,
              url: text(v.url),
              credentialRef: text(v.credentialRef),
              sphereKey: v.sphereKey || undefined,
              notes: text(v.notes),
            };
            if (editing === 'new') await createService(body);
            else await updateService(editing.id, body);
            void list.reload();
          }}
        />
      )}
    </Page>
  );
}

/** '$12/mo', '$99/yr', '$40 once', 'Usage', 'Free'. */
function costLabel(s: Service): string {
  if (s.costCents === null) return humanize(s.cadence);
  const per = { monthly: '/mo', yearly: '/yr', one_time: ' once', usage: ' usage', free: '' }[s.cadence] ?? '';
  return `${money(s.costCents)}${per}`;
}

function ServiceRow({ s, today, color, onEdit }: { s: Service; today: string; color: string; onEdit: () => void }): JSX.Element {
  const r = s.status !== 'cancelled' ? renewLabel(s.renewsOn, today) : null;
  return (
    <Row
      onClick={onEdit}
      ariaLabel={`Edit ${s.name}`}
      tone={s.status === 'cancelled' ? 'muted' : 'default'}
      title={s.name}
      sub={
        // The sphere dot leads the line, as on every other list row.
        <Meta color={color}>
          {s.status === 'trial' && <When tone="renew">Trial</When>}
          {s.status === 'trial' && r && ' · '}
          {r && <When tone={r.tone}>{r.label}</When>}
          {r || s.status === 'trial' ? null : humanize(s.cadence)}
        </Meta>
      }
      detail={<span className="num">{costLabel(s)}</span>}
      chevron={false}
    />
  );
}

/** The renewal date, labelled: 'renews Oct 12', 'renews today', 'renewal 3d late'. */
export function renewLabel(renewsOn: string | null, today: string): { label: string; tone: string } | null {
  const chip = dueChip(renewsOn, today);
  if (!chip) return null;
  if (chip.tone === 'overdue') return { label: `renewal ${chip.label.toLowerCase()}`, tone: chip.tone };
  const when = chip.label === 'Today' || chip.label === 'Tomorrow' ? chip.label.toLowerCase() : chip.label;
  return { label: `renews ${when}`, tone: chip.tone };
}
